import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { swallowNextClick } from '~/lib/swallow-click'

/**
 * The one floating card everything opened from the account wears: templates, Explore, a
 * template's page, settings and its sections, feedback, the weigh-in. Whichever of them is
 * showing, the card is the same card — content swaps inside it and the card eases to the new
 * size, which is what makes one panel read as morphing into the next rather than one dialog
 * replacing another.
 *
 * It stands where it was opened from rather than in one corner of the window: these are
 * opened from a control on a page — the way into settings, a row on You — and a card that
 * answers a press belongs beside the press. It is laid under that point, above it where the
 * window has no room under it, and never past the window's edges.
 *
 * It is a popover, not a modal: nothing behind it locks, and a click anywhere else dismisses
 * it. A modal dialog raised over it — a confirmation — suspends that: clicks inside the modal
 * and the Escape that closes it belong to the modal, not to this.
 */
export function PanelCard({
  label,
  at,
  leaving = false,
  showing,
  takeFocus = false,
  onDismiss,
  onMouseEnter,
  onMouseLeave,
  children,
}: {
  /** What the card is showing right now, for the accessibility tree. */
  label: string
  /**
   * Whether it is on its way out: it fades and blurs back up the way it came, and whatever
   * asked it to go waits for that before letting it go — the card cannot both play its leave
   * and be gone from the page.
   */
  leaving?: boolean
  /**
   * The near corner of whatever opened it, or undefined where nothing was pressed — a card
   * opened from an address rather than from a control, which stands in the middle of the
   * window instead.
   */
  at?: { x: number; y: number } | undefined
  /**
   * Which panel is showing, said as something that does not change while it is showing.
   *
   * It is not the label. A panel named after what it fetched — a template's page — is called
   * "Template" until the name arrives and then called by the name, and keying the content on
   * that would tear the panel down and build it again the moment it answered: the content it
   * had just painted would go back to a skeleton and every request it made would go twice.
   */
  showing: string
  /**
   * Whether the card was asked for rather than merely hovered into. Asked for, it takes the
   * focus and gives it back on the way out — it is drawn at the end of the body, so a
   * keyboard would otherwise have to cross the whole page to reach it. Hovered, it does not:
   * a pointer passing over the avatar must not pull the caret out of whatever is being typed.
   */
  takeFocus?: boolean
  /** Asked to leave — Escape or a click elsewhere. Guards are the current panel's business. */
  onDismiss: () => void
  onMouseEnter?: (() => void) | undefined
  onMouseLeave?: (() => void) | undefined
  children: ReactNode
}) {
  const card = useRef<HTMLDivElement>(null)
  const inner = useRef<HTMLDivElement>(null)
  const dismiss = useRef(onDismiss)
  useEffect(() => {
    dismiss.current = onDismiss
  })

  // The measured size of whatever is inside, which is the size the card eases toward.
  const [size, setSize] = useState<{ width: number; height: number } | null>(null)
  useLayoutEffect(() => {
    const element = inner.current
    if (element === null) {
      return
    }
    const measure = () => {
      setSize({ width: element.offsetWidth, height: element.offsetHeight })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!takeFocus) {
      return
    }
    const before = document.activeElement
    // Unless the panel has already placed it — feedback opens with the cursor in its field,
    // and a card that grabbed the focus back would be taking it off what it was asked for.
    if (card.current !== null && !card.current.contains(before)) {
      card.current.focus()
    }
    return () => {
      // Back where it came from, rather than on the body — which is where the browser puts it
      // when the element holding it is removed.
      if (before instanceof HTMLElement && document.body.contains(before)) {
        before.focus()
      }
    }
  }, [takeFocus])

  /*
   * Where it is laid: its own top corner on the top corner of whatever opened it, so the card
   * grows out of that control rather than standing near it; far enough up to fit where it is
   * too tall for the room below, and never past the window's edges.
   *
   * It is laid once and stays there. The card changes size as one panel morphs into the next,
   * and a card that were placed again on every change would jump about the window as it grew
   * and shrank — the size eases and the place does not, so the two read as two movements. Its
   * top holds and it grows downward from there, the way the corner card always did.
   */
  const atX = at?.x
  const atY = at?.y
  const [spot, setSpot] = useState<{ left: number; top: number } | null>(null)
  useLayoutEffect(() => {
    const element = card.current
    if (element === null || atX === undefined || atY === undefined) {
      return
    }
    const margin = 12
    const box = element.getBoundingClientRect()
    setSpot({
      left: Math.min(
        Math.max(margin, atX),
        Math.max(margin, window.innerWidth - box.width - margin),
      ),
      top: Math.min(
        Math.max(margin, atY),
        Math.max(margin, window.innerHeight - box.height - margin),
      ),
    })
  }, [atX, atY])

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (card.current?.contains(target)) {
        return
      }
      // A modal over the card — a confirmation, a full-screen photo — owns its own clicks.
      if (target instanceof Element && target.closest('[aria-modal="true"]') !== null) {
        return
      }
      // Putting the card away is the whole of what this press does. What it happened to land
      // on was in the card's shadow, and was not what anybody was reaching for.
      swallowNextClick()
      dismiss.current()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || document.querySelector('[aria-modal="true"]') !== null) {
        return
      }
      dismiss.current()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  /*
   * Drawn on the body rather than where it was written. The card lives inside the header, and
   * the header is its own stacking context — it is blurred, and a backdrop filter makes one —
   * so the card's layer was only ever the highest layer *inside the header*, which left it
   * under anything the page drew above the header. The template editor's side panel and its
   * rail are exactly that. Out here the layer means what it says.
   */
  return createPortal(
    <div className="float-holder" data-float={leaving ? 'out' : 'in'}>
      <div
        ref={card}
        role="dialog"
        aria-label={label}
        tabIndex={-1}
        // The highest layer the page has, per the web design spec: nothing a page draws —
        // the header, the set viewer, the rail — ever paints over the card. Modals portal
        // to the body too, so a confirmation raised from the card still stands above it.
        className={`fixed z-50 overflow-hidden rounded-3xl bg-surface-overlay shadow-2xl ring-1 ring-white/10 transition-[width,height] duration-200 ease-out ${
          at === undefined ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' : ''
        }`}
        style={{
          ...(size === null ? {} : { width: size.width, height: size.height }),
          // Off the window's edge until it has been measured, rather than in the middle for
          // a frame and then somewhere else.
          ...(at === undefined ? {} : { left: spot?.left ?? -9999, top: spot?.top ?? -9999 }),
        }}
        {...(onMouseEnter === undefined ? {} : { onMouseEnter })}
        {...(onMouseLeave === undefined ? {} : { onMouseLeave })}
      >
        {/*
          The panel declares its own width on this wrapper; the height is whatever its content
          comes to, capped so a long panel scrolls inside the card instead of past the window.
        */}
        <div
          ref={inner}
          className="w-fit overflow-y-auto"
          // Capped from where the card stands, so a panel taller than the room under it
          // scrolls inside the card rather than running off the foot of the window.
          style={{ maxHeight: `calc(100dvh - ${String((spot?.top ?? 72) + 12)}px)` }}
        >
          {/* Keyed by which panel is showing, so the next panel fades in as the card
              eases to its size — the swap and the resize read as one morph. */}
          <div key={showing} className="panel-morph-in">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
